import styled from '@emotion/styled';
import { LuxSelectablePill, LuxTypography } from '@gemini/fe-ds-core';
import { ChooseNewSlotType } from '@store/patientStore';
import {
  MobileBreakpoint,
  TabletBreakpoint,
} from '@utils/consts/breakpoints.consts';

export const ExternalWrapper = styled.div<{
  backgroundColor?: string;
}>`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background-color: ${props => props.backgroundColor || '#f2f2f2'};
  height: 100%;
`;

export const NavigationWrapper = styled.div<{
  hideHeader?: boolean;
}>`
  position: sticky;
  width: 100%;
  top: ${props => (props.hideHeader ? '0' : '4.75rem')};
  background-color: #f2f2f2;
  z-index: 2;
`;

export const StepperWrapper = styled.div<{
  stepperAlignment?: 'left' | 'center' | 'right';
}>`
  width: 100%;
  height: 3.25rem;
  padding: 0.5rem 1.5rem;
  justify-content: ${props =>
    props.stepperAlignment === 'right'
      ? 'space-between'
      : props.stepperAlignment};
  align-items: center;
  display: flex;
  background-color: rgba(255, 255, 255, 0.6);

  @media screen and (max-width: 768px) {
    height: 5.5rem;
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
    align-self: stretch;
  }
`;

export const BookingTitleDesktop = styled(LuxTypography)`
  display: flex;

  @media screen and (max-width: ${TabletBreakpoint}px) {
    display: none;
  }
`;

export const BookingTitleMobile = styled(LuxTypography)`
  display: none;

  @media screen and (max-width: ${TabletBreakpoint}px) {
    display: flex;
  }
`;

export const CentralDataContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;
  align-items: flex-start;
`;

export const RightContainer = styled.div<{ manageOrRescheduleMode?: boolean }>`
  z-index: 10;
  position: sticky;
  top: ${props => (props.manageOrRescheduleMode ? '4.25rem' : '9rem')};
  height: fit-content;
  max-height: calc(100vh - 9rem - 10rem);
  display: flex;
  flex-direction: column;
  gap: 1rem;
  @media screen and (max-width: ${TabletBreakpoint}px) {
    z-index: unset;
    position: unset;
    height: 100%;
    max-height: unset;
  }
`;

export const ContainerPill = styled.div<{
  chooseNewSlot: ChooseNewSlotType | boolean | undefined;
}>(
  {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    alignItems: 'flex-end',
    width: '100%',

    '@media screen and (max-width: 768px)': {
      display: 'none',
    },
  },
  ({ chooseNewSlot }) => ({
    justifyContent: chooseNewSlot ? 'space-between' : 'flex-end',
  }),
);

export const MobileContainerPill = styled.div<{
  chooseNewSlot: ChooseNewSlotType | boolean | undefined;
}>(
  {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    alignItems: 'flex-start',
    width: '100%',

    '@media screen and (min-width: 768px)': {
      display: 'none',
    },
  },
  ({ chooseNewSlot }) => ({
    justifyContent: chooseNewSlot ? 'space-between' : 'flex-start',
  }),
);

export const InnerContainer = styled.div<{ canceledPage?: boolean }>`
  display: grid;
  grid-template-columns: ${props =>
    props.canceledPage ? 'minmax(0, 1fr)' : 'minmax(0, 1fr) minmax(0, 0.5fr)'};

  gap: 1rem;
  width: 100%;
  @media screen and (max-width: 768px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const StepContainer = styled('div')<{
  noStyle?: boolean;
  marginTop?: string;
  backgroundColor?: string;
  borderColor?: string;
}>(
  {
    display: 'flex',
    flexDirection: 'column',
    height: 'fit-content',
    borderRadius: '0.5rem',
    gap: '0.5rem',
  },
  ({ noStyle, marginTop, borderColor, backgroundColor }) => ({
    border: noStyle ? '0px' : `1px solid ${borderColor || '#d9e4f5'}`,
    backgroundColor: noStyle
      ? 'transparent'
      : backgroundColor || 'rgba(255, 255, 255, 0.5)',
    marginTop: marginTop,

    '&:focus-visible': {
      border: noStyle ? '0px' : `1px solid ${borderColor || '#d9e4f5'}`,
      outline: 'none',
    },
  }),
);

export const ButtonsWrapper = styled.div<{
  step?: number;
  fromTyp?: boolean;
  onlySecondary?: boolean;
  flexDirection?: string;
}>`
  position: sticky;
  width: 100%;
  padding: 1rem;
  justify-content: ${props =>
    props.step === 0
      ? 'flex-end'
      : props.onlySecondary
        ? 'flex-start'
        : 'space-between'};
  display: flex;
  background-color: #fff;
  box-shadow: 0px -2px 8px -1px #1b242c14;
  z-index: 100;
  isolation: isolate;
  bottom: 0;
  @media screen and (max-width: ${MobileBreakpoint}px) {
    flex-direction: ${props =>
      props.flexDirection ? props.flexDirection : undefined};
    gap: 1rem;
  }
  @media screen and (max-width: ${MobileBreakpoint}px) {
    flex-direction: ${props => props.fromTyp && 'column-reverse'};
  }
  @media screen and (max-width: ${MobileBreakpoint}px) {
    > button:nth-child(2) {
      width: 100% !important;
    }
  }
`;

export const LeftButtonsWrapper = styled.div`
  width: 70%;
  display: flex;
  gap: 1rem;
  align-items: center;

  @media screen and (max-width: ${MobileBreakpoint}px) {
    flex-direction: column;
    width: 100%;
  }
`;

export const TabsWrapper = styled.div`
  padding-left: 1rem;
  padding-top: 0.5rem;
`;

export const NoWhiteSpaceLuxSelectablePill: React.ComponentType<any> = styled(
  LuxSelectablePill,
)`
  span {
    white-space: break-spaces;
  }
`;
